<script setup lang="ts">
    import { computed, inject, ref } from 'vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { type TagsProvide, type Tag } from '@shared/components/Tags/TagsTypes';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import { type Issue, statusName, type IssueStatistics } from './interfaces';
    import ListItemHeading from './ListItemHeading.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';
    import ListItemSent from './ListItemSent.vue';
    import ListItemSending from './ListItemSending.vue';
    import Countdown from '@shared/components/Countdown.vue';
    import ListItemMeta from './ListItemMeta.vue';
    import ListItemWaiting from './ListItemWaiting.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const winnerAnnounced = ref(false);
    const isSending = ref(false);
    const isSent = ref(false);

    const hasRecurringEditorAccess = scopeAccess.hasScope({
        Feature: 'Automations',
        Permission: Permissions.Execute,
    });

    const emit = defineEmits<{
        (ev: 'showPreview'): void;
        (ev: 'clickedIssueId'): void;
        (ev: 'pauseIssue'): void;
        (ev: 'pauseRunningIssue'): void;
        (ev: 'moveIssueToDrafts'): void;
        (ev: 'moveIssueToSent'): void;
        (ev: 'issueStatisticsLoaded', issue: IssueStatistics): void;
        (ev: 'updateSendingCount', currentTab: statusName): void;
        (ev: 'updateSentCount'): void;
    }>();

    const {
        issue,
        tags,
        isCreateNew,
        currentTab,
        loading,
        storedIssueStatistics = {},
    } = defineProps<{
        issue: Issue;
        tags?: TagsProvide;
        isCreateNew?: boolean;
        currentTab: statusName;
        loading: boolean;
        storedIssueStatistics?: {
            [key: string]: IssueStatistics;
        };
    }>();

    const showRecurringEditorBtn = computed(() => {
        return currentTab === statusName.recurring && hasRecurringEditorAccess;
    });

    const isScheduledOrRecurring = computed(() => {
        return currentTab === statusName.scheduled || currentTab === statusName.recurring;
    });

    const showScheduledInfo = computed(() => {
        return isScheduledOrRecurring.value && !isSending.value && !isSent.value;
    });

    const fromScheduledToSending = computed(() => {
        return isScheduledOrRecurring.value && isSending.value;
    });

    const fromSendingToSent = computed(() => {
        return isScheduledOrRecurring.value && isSent.value;
    });

    const startSending = () => {
        isSending.value = true;

        if (isScheduledOrRecurring.value) {
            emit('updateSendingCount', currentTab);
        }
    };

    const finishSending = () => {
        isSending.value = false;
        isSent.value = true;

        if (isScheduledOrRecurring.value) {
            emit('updateSentCount');
        }
    };

    const handleWinnerAnnounced = () => {
        winnerAnnounced.value = true;
        emit('updateSendingCount', currentTab);
    };
</script>

<template>
    <div class="flex-1" style="min-width: min(400px, 100%)">
        <ListItemHeading
            :issue="issue"
            :isCreateNew="isCreateNew"
            :currentTab="currentTab"
            @showPreview="emit('showPreview')"
            @clickedIssueId="emit('clickedIssueId')"></ListItemHeading>

        <p v-if="issue.Event">
            <strong>{{ $Resources.IssueEventHeading }}:</strong>
            {{ issue.Event.Name || $Resources.NoTitlePlaceholder }}
            <template v-if="issue.Event.Start">
                ({{
                    formatDateTime(issue.Event.Start, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })
                }})
            </template>
        </p>

        <p v-if="issue.IssueName">
            {{ issue.IssueName }}
        </p>

        <TagsList
            v-if="tags && issue.Tags.length > 0"
            :tags="issue.Tags"
            :selectedIds="tags.state.selectedIds"
            @toggleTag="(tag: Tag) => tags!.toggle(tag)"></TagsList>

        <ListItemMeta
            :Created="issue.Created"
            :CreatedBy="issue.CreatedBy"
            :LastModified="issue.LastModified"
            :LastModifiedBy="issue.LastModifiedBy"
            :Ended="issue.Ended"
            :IssueSender="issue.IssueSender || null"
            :currentTab="currentTab" />
    </div>

    <div class="align-self-center d-flex flex-wrap gap-sm align-items-center m-xs-b" v-if="showScheduledInfo">
        {{ $Resources.IssuesScheduledForSendingLabel }}
        {{ formatDateTime(issue.ScheduledForSending) }}

        <button type="button" class="btn btn-default btn-sm" :disabled="loading" @click="$emit('pauseIssue')">
            {{ $Resources.IssuesAbortScheduledButton }}
        </button>

        <a :href="`/Issues/${issue.IssueId}/Statistics?recurringEditor=true`" v-if="showRecurringEditorBtn" class="btn btn-default btn-sm">
            <span class="glyphicon glyphicon-repeat m-xxs-r"></span>
            {{ $Resources.EditRecurringSchedule }}
        </a>
    </div>

    <Countdown
        v-if="issue.ScheduledForSending > new Date().toISOString()"
        :countdownTo="issue.ScheduledForSending"
        :hideTimer="true"
        @countdownFinished="startSending"></Countdown>

    <div v-if="currentTab === statusName.paused || currentTab === statusName.failed" class="align-self-center m-xs-b">
        <div class="btn-group" role="group">
            <button type="button" class="btn btn-default btn-sm" :disabled="loading" @click="$emit('moveIssueToDrafts')">
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
                {{ $Resources.MoveIssueToDraftsButton }}
            </button>
            <button type="button" class="btn btn-default btn-sm" :disabled="loading || currentTab === statusName.failed" @click="$emit('moveIssueToSent')">
                {{ $Resources.MoveIssueToSentButton }}
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </div>
    </div>

    <ListItemWaiting v-if="currentTab === statusName.issueVariation && !winnerAnnounced" :issueId="issue.IssueId" @winnerAnnounced="handleWinnerAnnounced" />

    <ListItemSending
        v-if="currentTab === statusName.sending || fromScheduledToSending || winnerAnnounced"
        :issueId="issue.IssueId"
        :loading="loading"
        @pauseIssue="emit('pauseIssue')"
        @pauseRunningIssue="emit('pauseRunningIssue')"
        @isSent="finishSending" />

    <ListItemSent
        v-if="currentTab === statusName.sent || fromSendingToSent"
        :issueId="issue.IssueId"
        :resendToExistingRecipients="issue.ResendToExistingRecipients"
        :storedIssueStatistics="storedIssueStatistics"
        @issueStatisticsLoaded="(issue: IssueStatistics) => emit('issueStatisticsLoaded', issue)" />
</template>
